import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio,
    Typography,
    Box,
    CircularProgress,
} from "@mui/material";
import { axios_tenant } from "../../../../axios";
import { toast } from "react-toastify";
import Button from "./atoms/Button";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import "../../../pages/CollaborationSpace/OAWorkflow/OAWorkflow.css";

const BASE_URL = "/collaboration/workflow";

const AddOAUserDialog = ({ open, onClose, onUserAdded, roles = [], oaUsers = [], workType = "OA Shell" }) => {
    const [email, setEmail] = useState("");
    const [roleId, setRoleId] = useState("");
    const [loading, setLoading] = useState(false);

    const resetForm = () => {
        setEmail("");
        const availableRoles = roles.filter(r => r.roleName !== "Admin");
        setRoleId(availableRoles.length > 0 ? availableRoles[0].id : "");
    };

    // Set default roleId when roles load
    useEffect(() => {
        if (roles.length > 0 && !roleId) {
            const availableRoles = roles.filter(r => r.roleName !== "Admin");
            setRoleId(availableRoles.length > 0 ? availableRoles[0].id : "");
        }
    }, [roles, roleId]);

    // Reset email when dialog opens
    useEffect(() => {
        if (!open) {
            setEmail("");
        }
    }, [open]);

    const handleSubmit = async () => {
        const trimmedEmail = email.trim().toLowerCase();
        if (!trimmedEmail) {
            toast.warn("Email is required");
            return;
        }
        // Basic email validation
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
            toast.warn("Please enter a valid email address");
            return;
        }
        if (!roleId) {
            toast.warn("Please select a role");
            return;
        }

        // Check if user is already in OA Shell users list
        const alreadyAdded = oaUsers.some(u => String(u.email).toLowerCase() === trimmedEmail);
        if (alreadyAdded) {
            toast.warn(`This user already has a ${workType} role assigned`);
            return;
        }

        try {
            setLoading(true);
            // COL-148: silent so the shared axios interceptor doesn't toast the
            // same error a second time — the catch below shows it once.
            const res = await axios_tenant.post(`${BASE_URL}/oashell-users`, {
                email: trimmedEmail,
                roleId,
                workType,
            }, { silent: true });

            // If user is not in tenant, trigger the gateway invite flow
            if (res.data?.userNotInTenant) {
                const oaRoleName = res.data.roleName; // e.g. "Processor", "QA", "Application Manager"
                try {
                    await axios_tenant.post("/user/sendInvitation", {
                        email: trimmedEmail,
                        role: "user",
                        apps: ["COLLABORATE"],
                        ...(workType === "Post Grant Support" ? { pgsRole: oaRoleName } : { oaRole: oaRoleName }),
                        workType, // Pass workType to gateway as well
                    });
                    toast.success(`Invitation sent to ${trimmedEmail} with ${oaRoleName} role`);
                } catch (inviteErr) {
                    const inviteMsg = inviteErr.response?.data?.message || "Failed to send invitation";
                    toast.error(inviteMsg);
                }
                resetForm();
                onUserAdded?.();
                return;
            }

            const msg = res.data?.message || "User added successfully";
            toast.success(msg);
            resetForm();
            onUserAdded?.();
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to add user";
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const outerTheme = useTheme();

    const localTheme = React.useMemo(() => createTheme(outerTheme, {
        typography: {
            fontFamily: 'Lato, sans-serif',
        },
    }), [outerTheme]);

    return (
        <ThemeProvider theme={localTheme}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="xs"
                fullWidth
                PaperProps={{ className: "oa-lato-font", sx: { borderRadius: 2 } }}
            >
                <DialogTitle>
                    <Typography variant="h6" fontWeight={600}>Add User</Typography>
                </DialogTitle>

                <DialogContent>
                    <TextField
                        label="Email"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter user's email address"
                        sx={{ mt: 1, mb: 1 }}
                        autoFocus
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 3 }}>
                        If the user is not part of the tenant, an invitation will be sent.
                    </Typography>

                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        Product Access
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                        Select a role to assign to the user.
                    </Typography>

                    <RadioGroup
                        value={roleId}
                        onChange={(e) => setRoleId(Number(e.target.value))}
                        row
                    >
                        {roles.filter(r => r.roleName !== "Admin").map(r => (
                            <FormControlLabel
                                key={r.id}
                                value={r.id}
                                control={<Radio size="small" sx={{ color: '#E2E8F0', '&.Mui-checked': { color: '#005CB9' } }} />}
                                label={r.roleName}
                            />
                        ))}
                    </RadioGroup>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleClose} variant="secondary" disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="primary"
                        disabled={loading || !email.trim() || !roleId}
                    >
                        {loading ? <CircularProgress size={20} color="inherit" /> : "Submit"}
                    </Button>
                </DialogActions>
            </Dialog>
        </ThemeProvider>
    );
};

export default AddOAUserDialog;
